import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Roles } from "~enums/roles";
import { useApi } from "~hooks/apiHooks/useApi";
import type { TransformedSeasonRoster } from "~pages/Roster/types/transformedSeasonRoster";
import { transformRoster } from "~pages/Roster/utilities/transformRoster";
import type { RoleResponse } from "~types/role";
import type { RosterResponse } from "~types/roster";

const seasonRosterParams = new URLSearchParams({
  include: ["entities", "seasons", "persons"].join(","),
  limit: "1000",
  offset: "0",
});

type Props = {
  customParams: Record<string, string>;
  entityId?: string;
  fixtureId?: string;
};

const useFetchRoster = ({ customParams, entityId, fixtureId }: Props) => {
  const request = useApi();
  const { organizationId, sport, seasonId } = useParams();

  const params = new URLSearchParams({
    ...customParams,
  });

  const captainParams = new URLSearchParams({
    limit: "1000",
    offset: "0",
    role: Roles.Captain,
    ...(entityId && { entityId }),
    ...(seasonId && !fixtureId && { seasonId }),
  });

  const queryKey = fixtureId ? "fixtureRoster" : "seasonRoster";

  const url = fixtureId
    ? `${sport}/o/${organizationId}/fixtures/${fixtureId}/entities/${entityId}/roster?${params.toString()}`
    : `${sport}/o/${organizationId}/seasons/${seasonId}/entities/${entityId}/roster?${params.toString()}`;

  const rolesUrl = fixtureId
    ? `${sport}/o/${organizationId}/fixtures/${fixtureId}/roles?${captainParams.toString()}`
    : `${sport}/o/${organizationId}/roles?${captainParams.toString()}`;

  return useQuery<Array<TransformedSeasonRoster>>({
    queryKey: [queryKey, sport, organizationId, seasonId, entityId, fixtureId, params.toString()],
    queryFn: async () => {
      const promises: Promise<RosterResponse | RoleResponse>[] = [request<RosterResponse>(url), request<RoleResponse>(rolesUrl)];
      if (fixtureId) {
        promises.push(
          request<RosterResponse>(
            `${sport}/o/${organizationId}/seasons/${seasonId}/entities/${entityId}/roster?${seasonRosterParams.toString()}`,
          ),
        );
      }

      const [response, captainResponse, seasonRosterResponse] = await Promise.all(promises);

      let transformed: TransformedSeasonRoster[] = transformRoster(response as RosterResponse, captainResponse as RoleResponse);
      if (fixtureId) {
        const seasonTransformed = transformRoster(seasonRosterResponse as RosterResponse);
        transformed = transformed.map((item) => {
          const seasonRosterData = seasonTransformed?.find((seasonItem) => seasonItem.personId === item.personId);
          return { ...item, status: seasonRosterData?.status };
        });
      }
      return transformed;
    },
    enabled: !!entityId,
  });
};

export default useFetchRoster;
